/**
 * Verifica se o role é o perfil de administrador (por flag, slug ou nome).
 * Compatível com role slug 'administrador' (single-tenant) ou 'admin'.
 * @param {Object} role - Objeto role
 * @returns {boolean}
 */
const isAdminRole = role => {
  if (!role) return false;
  // DB pode retornar is_admin como 1/0 (number)
  if (role.is_admin === true || role.is_admin === 1) return true;
  const slug = (role.slug || '').toLowerCase();
  const name = (role.name || '').toLowerCase();
  return (
    slug === 'admin' ||
    slug === 'administrador' ||
    name === 'administrador' ||
    name.includes('administrador')
  );
};

/**
 * Obtém o role do usuário (single-tenant: role direto no user).
 * @param {Object} user - Dados do usuário
 * @returns {Object|null} role
 */
const getEffectiveRole = user => (user ? user.role ?? null : null);

/**
 * Verifica se o role tem a permissão (aceita vários formatos do backend).
 * - Array de objetos: [{ key: 'member.view' }, ...]
 * - Array de strings: ['member.view', ...]
 * - Objeto: { 'member.view': true } ou { member: { view: true } }
 */
const roleHasPermission = (role, permissionKey) => {
  const p = role.permissions;
  if (!p || typeof p !== 'object') return false;

  if (Array.isArray(p)) {
    return p.some(
      item => item === permissionKey || (item && typeof item === 'object' && item.key === permissionKey)
    );
  }

  // Objeto: chave direta ou aninhada (ex.: p['member.view'] ou p.member?.view)
  if (p[permissionKey] === true) return true;
  const parts = permissionKey.split('.');
  if (parts.length === 2) {
    const [module, action] = parts;
    return p[module]?.[action] === true || p[module] === true;
  }
  return false;
};

/**
 * Como `roleHasPermission`, mas **não** trata `p[module] === true` como todas as ações do módulo.
 * Use para checagens “estritas” (ex.: `products.manage` sem atalho de `permissions.products === true`).
 */
const roleHasPermissionStrict = (role, permissionKey) => {
  const p = role.permissions;
  if (!p || typeof p !== 'object') return false;

  if (Array.isArray(p)) {
    return p.some(
      item => item === permissionKey || (item && typeof item === 'object' && item.key === permissionKey),
    );
  }

  if (p[permissionKey] === true) return true;
  const parts = permissionKey.split('.');
  if (parts.length === 2) {
    const [module, action] = parts;
    return p[module]?.[action] === true;
  }
  return false;
};

/**
 * Helper para verificar permissões do usuário (usa user.role do login/me).
 * @param {Object} user - Dados do usuário (com user.role)
 * @param {string} permissionKey - Chave da permissão
 * @returns {boolean} - Se o usuário tem a permissão
 */
export const can = (user, permissionKey) => {
  const role = getEffectiveRole(user);
  if (!role) return false;

  // Perfil administrador tem acesso total (is_admin: true no role)
  if (isAdminRole(role)) return true;

  return roleHasPermission(role, permissionKey);
};

/**
 * Igual a `can`, exceto que não aplica o atalho `permissions[module] === true` para chaves `module.action`.
 * Administrador de sistema (`is_admin` / slug admin) continua com acesso total.
 */
export const canStrict = (user, permissionKey) => {
  const role = getEffectiveRole(user);
  if (!role) return false;
  if (isAdminRole(role)) return true;
  return roleHasPermissionStrict(role, permissionKey);
};

/**
 * Verificar se o usuário é admin (por role administrador).
 * @param {Object} user - Dados do usuário
 * @returns {boolean} - Se o usuário é admin
 */
export const isAdmin = user => {
  const role = getEffectiveRole(user);
  return role ? isAdminRole(role) : false;
};

/**
 * Verificar se o usuário pode gerenciar membros
 * @param {Object} user - Dados do usuário
 * @returns {boolean} - Se o usuário pode gerenciar membros
 */
export const canManageMembers = user => {
  return can(user, 'member.manage') || can(user, 'member.update') || isAdmin(user);
};

/**
 * Verificar se o usuário pode gerenciar perfis
 * @param {Object} user - Dados do usuário
 * @returns {boolean} - Se o usuário pode gerenciar perfis
 */
export const canManageRoles = user => {
  return (
    can(user, 'roles.manage') ||
    can(user, 'roles.update') ||
    can(user, 'roles.create') ||
    isAdmin(user)
  );
};

/**
 * Helpers por módulo para Navegar/Criar/Editar/Excluir (CRUD)
 */
export const canNavigateModule = (user, moduleKey) => {
  return (
    isAdmin(user) ||
    can(user, `${moduleKey}.view`) ||
    can(user, `${moduleKey}.create`) ||
    can(user, `${moduleKey}.update`) ||
    can(user, `${moduleKey}.delete`)
  );
};

export const canCreateInModule = (user, moduleKey) => {
  return (
    isAdmin(user) ||
    can(user, `${moduleKey}.manage`) ||
    can(user, `${moduleKey}.create`) ||
    can(user, `${moduleKey}.update`)
  );
};

export const canEditInModule = (user, moduleKey) => {
  return (
    isAdmin(user) ||
    can(user, `${moduleKey}.manage`) ||
    can(user, `${moduleKey}.update`)
  );
};

export const canDeleteInModule = (user, moduleKey) => {
  return (
    isAdmin(user) ||
    can(user, `${moduleKey}.manage`) ||
    can(user, `${moduleKey}.delete`) ||
    can(user, `${moduleKey}.update`)
  );
};

/**
 * Obter todas as permissões do usuário (single-tenant: user.role).
 * @param {Object} user - Dados do usuário
 * @returns {Array} - Array de chaves de permissões
 */
export const getUserPermissions = user => {
  const role = getEffectiveRole(user);
  if (!role) return [];

  // Perfil administrador tem todas as permissões
  if (isAdminRole(role)) return ['*'];

  const p = role.permissions;
  if (!p || typeof p !== 'object') return [];

  if (Array.isArray(p)) {
    return p.map(item =>
      typeof item === 'string' ? item : item?.key
    ).filter(Boolean);
  }

  // Objeto: chaves com valor true (ex.: { 'member.view': true })
  return Object.keys(p).filter(k => p[k] === true);
};
